import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';
import { useRoomContext } from '@src/contexts';
import { useRoomConfigStore, useRoomStore, useCalculatorStore, useWebinarRoomStore } from '@src/stores';
import { EMainConfigKeys, TConfigKey, ECalculatorConfigKeys, ECalculatorSocketEvents, EMeetingType } from '@src/types';

import { CONFIG_TOGGLE_MAP } from '@src/constants';

const useGeneralSettings = () => {
  const { canUserManageSettings } = useRoomContext();

  const { requestInfo, resetRequestInfo } = useRoomStore(
    useShallow((state) => ({
      requestInfo: state.requestInfo,
      resetRequestInfo: state.resetRequestInfo,
      meetingType: state.meetingType,
    }))
  );

  const { userTypeRoomConfig } = useRoomConfigStore(
    useShallow((state) => ({
      userTypeRoomConfig: state.userTypeRoomConfig,
    }))
  );

  const buildServerPayload = (key: TConfigKey, value: boolean) => {
    const configMeta = CONFIG_TOGGLE_MAP[key];
    if (!configMeta) {
      console.warn(`No config metadata for key: ${key}`);
      return null;
    }

    const { section, roles } = configMeta;

    const payload: Record<string, any> = {};
    roles.forEach((role) => {
      payload[role] = { [section]: { [key]: value } };
    });

    return { payload, section };
  };

  const setNewConfig = (section: string, config: Partial<Record<TConfigKey, boolean>>) => {
    switch (section) {
      case 'chat':
        return useRoomConfigStore.getState().setChatConfig(config);
      case 'main':
        return useRoomConfigStore.getState().setMainConfig(config);
      case 'calculator':
        return;
      default:
        throw new Error(`Unknown config section: ${section}`);
    }
  };

  const handleToggleChange = async (key: TConfigKey, value: boolean) => {
    if (key === EMainConfigKeys.StartScreenButton && value) {
      resetRequestInfo();
      requestInfo.forEach(({ requestId }) => toast.dismiss(requestId));
    }

    if (key === ECalculatorConfigKeys.shareCalculatorButton && !value) {
      const sharedCalculatorData = useCalculatorStore.getState().sharedCalculatorData;

      if (sharedCalculatorData.isOpen && sharedCalculatorData.owner) {
        try {
          await SocketService.request(ECalculatorSocketEvents.STOP_SHARING_CALCULATOR, {
            forceStoppedByHost: true,
            targetPeerId: sharedCalculatorData.owner.peer_id,
          });
          useCalculatorStore.getState().resetSharedCalculatorData();
        } catch (err) {
          console.error('Error force-stopping calculator sharing:', err);
        }
      }
    }

    const result = buildServerPayload(key, value);
    if (!result) return;

    const { payload, section } = result;

    try {
      const response = await SocketService.request('updateRoomConfig', payload);

      if (!response?.success) {
        console.error('updateRoomConfig failed:', response);
        return;
      }

      setNewConfig(section, { [key]: value });
    } catch (err) {
      console.error('Error updating config:', err);
    }
  };

  useEffect(() => {
    if (canUserManageSettings) {
      SocketService.request('getRoomConfigByUserType')
        .then((response) => useRoomConfigStore.getState().setUserTypeRoomConfig(response))
        .catch((err) => console.error('getRoomConfigByUserType error:', err));
    }
  }, [canUserManageSettings]);

  return { canUserManageSettings, handleToggleChange, userTypeRoomConfig };
};

export default useGeneralSettings;
